// The report renderer is shared by user-interface (i18n-aware, es/en) and
// admin-interface (Spanish-only staff tooling, no i18next). Rather than force
// i18next into admin, the renderer asks a *registered* translator for its
// labels and falls back to the Spanish locale in ./locales when none is
// registered — which is exactly the admin case.
//
// user-interface registers i18next at startup (see Common/i18n), which also
// merges ./locales into its resources, so the strings have a single home and
// the report still follows the user's language there. Those files are plain
// JSON, nested exactly like the app's own locale files, for the same reason:
// they are translations, and that is how this repo stores translations.

import esLocale from "./locales/es.json";

type Params = Record<string, string | number> | undefined;

export type ReportTranslator = (key: string, params?: Params) => string;

/** Flattens the nested bundle into the dotted keys the renderer asks for. */
const flatten = (node: unknown, prefix = ""): Record<string, string> => {
  if (typeof node === "string") return { [prefix]: node };
  if (node == null || typeof node !== "object") return {};
  return Object.entries(node as Record<string, unknown>).reduce<Record<string, string>>(
    (acc, [key, value]) => Object.assign(acc, flatten(value, prefix ? `${prefix}.${key}` : key)),
    {},
  );
};

const DEFAULT_STRINGS_ES = flatten(esLocale);

const interpolate = (template: string, params: Params): string =>
  params
    ? template.replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
        params[name] === undefined ? match : String(params[name]))
    : template;

// i18next resolves a plural key by appending _one/_other to the name it is
// given; the fallback has to do the same or a counted label comes out as its
// own key in admin-interface, which registers no translator.
const lookup = (key: string, params: Params): string | undefined => {
  const direct = DEFAULT_STRINGS_ES[key];
  if (direct !== undefined) return direct;
  const count = params?.count;
  if (typeof count !== "number") return undefined;
  return DEFAULT_STRINGS_ES[`${key}_${count === 1 ? "one" : "other"}`];
};

const fallback: ReportTranslator = (key, params) =>
  interpolate(lookup(key, params) ?? key, params);

let translator: ReportTranslator = fallback;

/**
 * Registers the host app's translator for every label the report renderer
 * emits. Call it once at startup, before the first report renders.
 *
 * @param fn Translator taking the same (key, params) i18next's `t` takes.
 */
export function setReportTranslator(fn: ReportTranslator): void {
  translator = fn;
}

/**
 * Translates one report label.
 *
 * @param key Dotted translation key, as nested in ./locales.
 * @param params Interpolation values for the `{{placeholders}}` in the string.
 * @returns The translated string, or the Spanish default when no translator is registered.
 */
export function t(key: string, params?: Params): string {
  return translator(key, params);
}
